/**
 * Age, expressed the way breeders talk about it.
 *
 * A bird's first weeks matter in days, its first year in months, and after that only in
 * years — "18 months" is how a hen is described, "547 days" is not. Dates come from the
 * API as plain calendar dates (`YYYY-MM-DD`) because a bird hatches on a day, not at an
 * instant, so nothing here touches timezones.
 */

/** A calendar date with no time and no zone, as the API sends it. */
export type PlainDate = `${number}-${number}-${number}` | string;

const MS_PER_DAY = 86_400_000;

/** Parses `YYYY-MM-DD` as a UTC instant, so arithmetic never crosses a DST boundary. */
function toUtc(date: PlainDate): number {
  const [year, month, day] = date.split("-").map(Number);

  if (year === undefined || month === undefined || day === undefined || Number.isNaN(year)) {
    throw new RangeError(`Expected a YYYY-MM-DD date, got "${date}".`);
  }

  return Date.UTC(year, month - 1, day);
}

/** Whole days between two calendar dates. Negative when `to` precedes `from`. */
export function daysBetween(from: PlainDate, to: PlainDate): number {
  return Math.round((toUtc(to) - toUtc(from)) / MS_PER_DAY);
}

/** Age in whole days on `asOf`. */
export function ageInDays(hatchDate: PlainDate, asOf: PlainDate): number {
  return daysBetween(hatchDate, asOf);
}

/**
 * Age as a breeder would say it. Days up to eight weeks — the window where ringing,
 * fledging and weaning all happen and a single day matters — then months, then years.
 */
export function describeAge(hatchDate: PlainDate, asOf: PlainDate): string {
  const days = ageInDays(hatchDate, asOf);

  if (days < 0) {
    return "Not yet hatched";
  }

  if (days === 0) {
    return "Hatched today";
  }

  if (days < 56) {
    return days === 1 ? "1 day" : `${days} days`;
  }

  const months = Math.floor(days / 30.44);
  if (months < 24) {
    return months === 1 ? "1 month" : `${months} months`;
  }

  const years = Math.floor(days / 365.25);
  return years === 1 ? "1 year" : `${years} years`;
}

/**
 * Whether a bird is old enough to breed. Canaries and goldfinches are paired in their
 * first spring after hatching, so this is a floor rather than a recommendation — the
 * breeder still decides, and a bird hatched late in the season is often held back a year.
 */
export function isOfBreedingAge(hatchDate: PlainDate, asOf: PlainDate, minimumDays = 300): boolean {
  return ageInDays(hatchDate, asOf) >= minimumDays;
}
